import { useMemo } from 'react';

import { ENFORCED_PLAN_FEATURES, Permission } from '@bookora/shared';

import { useAiImportEntitlement } from '@/api/generated/ai-import/ai-import';
import { queryKeys } from '@/api/query-keys';
import { useSessionStore } from '@/auth/token-store';
import { planLockOf, usePlanAccess, type PlanAccessResponse } from '@/features/plan/plan-access';
import { useCan } from '@/permissions/use-can';

import { rememberTourFeatures } from './resolve-steps';
import { NO_TOUR_FEATURES, type TourFeature } from './tours/types';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  TOUR FEATURES — and why they FAIL CLOSED
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Feature gates a tour (`requiresFeature`) or a single step may declare. Every
 * resolver runs unconditionally (hook order); a resolver the caller cannot use
 * is simply `false`.
 *
 * The lock UI fails OPEN (a screen is never hidden behind a failed read — the
 * server's 402 is the gate). Tours are the opposite: a tour that spotlights a
 * tab the clinic's plan locks, or tells a simple-stock clinic to type a batch
 * number, is worse than a tour one step shorter. So in the record this hook
 * returns — what `tourAllowed` and every whole-tour gate reads — each plan
 * feature is `false` until plan access has ANSWERED that it is on, and
 * `false` again if the read failed (panel-locked R15). Once it has answered,
 * the record is also handed to the step filter (`rememberTourFeatures` in
 * `resolve-steps.ts`), which drops a step about a locked tab.
 *
 *  · `aiImport`: the entitlement endpoint is asked only by `import.ai`
 *    holders (anyone else gets a 403 and has no import screens anyway). The
 *    review tour needs the feature AVAILABLE on this installation — not quota
 *    left: a job already on screen is reviewed whatever the remaining pages.
 *  · the enforced plan features: on only when plan access says the feature is
 *    in the clinic's effective set AND no lock (locked or read-only) names it.
 *    `enforced: false` (no live subscription) → the server marks every feature
 *    on, and so do the tours.
 *  · `raas`: the clinic is billed «نتیجه‌محور» (`billingModel === 'RAAS'`).
 */
export function tourFeaturesFrom(
  access: PlanAccessResponse | undefined,
  aiImport: boolean,
): Record<TourFeature, boolean> {
  const out: Record<TourFeature, boolean> = { ...NO_TOUR_FEATURES, aiImport };
  if (!access) return out;
  for (const feature of ENFORCED_PLAN_FEATURES) {
    if (feature === 'aiImport') continue;
    // `enforced: false` — no live subscription — gates nothing.
    out[feature] =
      !access.enforced ||
      (access.features[feature] === true && planLockOf(access, feature).state === 'open');
  }
  out.raas = access.billingModel === 'RAAS';
  return out;
}

export function useTourFeatures(): Record<TourFeature, boolean> {
  const canImport = useCan(Permission.IMPORT_AI);
  const clinicId = useSessionStore((s) => s.activeClinicId);
  const entitlement = useAiImportEntitlement({
    query: {
      queryKey: queryKeys.aiImport.entitlement(clinicId),
      enabled: canImport && Boolean(clinicId),
      staleTime: 60_000,
      retry: false,
    },
  });
  const planAccess = usePlanAccess();
  // A failed read is unknown, not «everything on».
  const access = planAccess.isError ? undefined : planAccess.data;
  const aiImport = canImport && entitlement.data?.available === true;

  const features = useMemo(() => tourFeaturesFrom(access, aiImport), [access, aiImport]);
  // Recorded for the step filter (`resolve-steps.ts`) in THIS render, before
  // the caller resolves any step — only once plan access has answered, so a
  // step is never dropped on a guess. Idempotent: same clinic, same record.
  if (access) rememberTourFeatures(clinicId, features);
  return features;
}
